import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface FinancingLine {
  id: string;
  user_id: string;
  type: 'Prêt bancaire' | 'Subvention' | 'Autofinancement' | 'Autre';
  label: string;
  amount: number;
  rate?: number;
  duration_years?: number;
  start_date?: string;
  notes?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export function useFinancingPlan() {
  const [lines, setLines] = useState<FinancingLine[]>([]);
  const [loading, setLoading] = useState(true);
  const { effectiveUserId } = useAuth();

  const load = async () => {
    if (!effectiveUserId) return;
    setLoading(true);
    const { data } = await supabase
      .from('financing_plan_lines')
      .select('*')
      .eq('user_id', effectiveUserId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    setLines(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [effectiveUserId]);

  const addLine = async (line: Omit<FinancingLine, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!effectiveUserId) return;
    const { error } = await supabase
      .from('financing_plan_lines')
      .insert([{ ...line, user_id: effectiveUserId }]);
    if (error) throw error;
    await load();
  };

  const updateLine = async (id: string, updates: Partial<Omit<FinancingLine, 'id' | 'user_id' | 'created_at'>>) => {
    const { error } = await supabase
      .from('financing_plan_lines')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', effectiveUserId);
    if (error) throw error;
    await load();
  };

  const removeLine = async (id: string) => {
    const { error } = await supabase
      .from('financing_plan_lines')
      .delete()
      .eq('id', id)
      .eq('user_id', effectiveUserId);
    if (error) throw error;
    await load();
  };

  return { lines, loading, addLine, updateLine, removeLine, refresh: load };
}
